import {
  Alert,
  Backdrop,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  Icon,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItem,
  MenuItem,
  Select,
  Snackbar,
  TextField,
} from "@mui/material";
import axios from "axios.js";
import usePageTitle from "app/pageTitle";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";
// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import { useAppLocation } from "agent_location_context";
import GeoTaggedCamera from "app/components/gps_camera";

function DawOnboardRetailer() {
  const pageTitle = `Display & Win - Onboard Retailer`;
  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const { applocation, permissionStatus, updateAppLocation } = useAppLocation();

  const [statesAndLocations, setStatesAndLocations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [retailerName, setRetailerName] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [emailAddress, setEmailAddress] = useState(null);
  const [retailerAddress, setRetailerAddress] = useState(null);
  const [visitPhoto, setVisitPhoto] = useState(null);
  const [openCamera, setOpenCamera] = useState(false);
  const [createdRetailerRef, setCreatedRetailerRef] = useState(null);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`agents/daw/${ref}/onboard/init.json`)
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            setStatesAndLocations(result.data.data.states);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine
                ? ""
                : "Make sure you have an active internet connection"
            }`,
          );
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  async function onboard() {
    if (retailerName === null) {
      setSeverity("error");
      setErrMsg("Retailer name is required.");
      setOpen(true);
      return false;
    }

    if (phoneNumber === null) {
      setSeverity("error");
      setErrMsg("Retailer phone number is required.");
      setOpen(true);
      return false;
    }

    if (retailerAddress === null) {
      setSeverity("error");
      setErrMsg("Retailer address is required.");
      setOpen(true);
      return false;
    }

    if (!selectedState || !selectedLocation) {
      setSeverity("error");
      setErrMsg("Incomplete State and Lga.");
      setOpen(true);
      return false;
    }

    if (visitPhoto === null) {
      setSeverity("error");
      setErrMsg("Photo is required.");
      setOpen(true);
      return;
    }

    if (!applocation?.lat || !applocation?.lng) {
      setSeverity("error");
      setErrMsg(
        "Unable to capture GPS coordinates. Enable location on this device",
      );
      setOpen(true);
      return false;
    }

    setShowLoading(true);
    const json = {
      campaign: ref,
      name: retailerName,
      phone_number: phoneNumber,
      email_address: emailAddress,
      address: retailerAddress,
      state: selectedState,
      lga: selectedLocation,
      lat: applocation?.lat,
      lng: applocation?.lng,
      photo: visitPhoto.image,
    };

    await axios
      .post("agents/daw/retailers/onboard.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setCreatedRetailerRef(result.data.data.reference);
          setErrMsg(result.data.data.message);
          setOpenSuccessDialog(true);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine
              ? ""
              : "Make sure you have an active internet connection"
          }`,
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      {createdRetailerRef && (
        <Dialog open={openSuccessDialog} maxWidth="xs" fullWidth>
          <DialogContent>
            <MDBox mt={3} align="center">
              <MDBox>
                <MDTypography fontSize={64}>
                  <Icon color="success">check_circle_outline</Icon>
                </MDTypography>
                <MDTypography variant="subtitle2" fontSize={14} mt={-1}>
                  {errorMsg}
                </MDTypography>
              </MDBox>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton
              variant="contained"
              color="primary"
              size="small"
              onClick={() =>
                navigate(
                  `/activations/display-win/retailers/${createdRetailerRef}`,
                )
              }
            >
              View Retailer
            </MDButton>
          </DialogActions>
        </Dialog>
      )}

      <MDBox mt={2} mb={5}>
        <MDBox display="flex" justifyContent="space-between" mb={2}>
          <MDBox>
            <Button
              color="dark"
              size="small"
              startIcon={<Icon>west</Icon>}
              onClick={() => {
                if (window.history.length > 1) {
                  window.history.back();
                } else {
                  navigate(`/activations/display-win/${ref}`);
                }
              }}
            >
              Back
            </Button>
          </MDBox>
        </MDBox>
        <Card>
          <MDBox p={2}>
            <MDBox align="center">
              <MDTypography variant="h6">Onboard Retailer</MDTypography>
            </MDBox>
            <MDBox mt={2}>
              <MDInput
                name="business_name"
                margin="dense"
                label="Retailer Name"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setRetailerName(e.target.value)}
              />
            </MDBox>

            <MDBox mt={2}>
              <MDInput
                name="email_address"
                margin="dense"
                label="Email Address (Optional)"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setEmailAddress(e.target.value)}
              />
            </MDBox>
            <MDBox mt={2}>
              <MDInput
                name="phone_number"
                margin="dense"
                label="Phone Number"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
            </MDBox>
            <MDBox mt={4}>
              <MDInput
                margin="dense"
                label="Address"
                type="text"
                multiline
                minRows={3}
                fullWidth
                variant="outlined"
                onChange={(e) => setRetailerAddress(e.target.value)}
              />
            </MDBox>
            <MDBox mt={3}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>State</InputLabel>
                <Select
                  label="State"
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon>location_on</Icon>
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => {
                    const stateRef = e.target.value;
                    setSelectedState(stateRef);
                    setSelectedLocation(null);

                    const stateObj = statesAndLocations.find(
                      (st) => st.reference === stateRef,
                    );
                    setLocations(stateObj?.lgas || []);
                  }}
                  value={selectedState}
                >
                  {statesAndLocations.map((st) => (
                    <MenuItem key={st.reference} value={st.reference}>
                      {st.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MDBox>
            <MDBox mt={4}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>LGA</InputLabel>
                <Select
                  label="LGA"
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  value={selectedLocation}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon>location_on</Icon>
                      </InputAdornment>
                    ),
                  }}
                >
                  {locations.map((st) => (
                    <MenuItem key={st.reference} value={st.reference}>
                      {st.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MDBox>
            <MDBox mt={5} align="center">
              <GeoTaggedCamera
                open={openCamera}
                onClose={() => setOpenCamera(false)}
                onCapture={(data) => {
                  setVisitPhoto(data);
                  setOpenCamera(false);
                }}
              />
              <MDTypography variant="caption">
                Take a picture of the storefront.
              </MDTypography>
            </MDBox>
            <MDBox mt={5}>
              <MDButton
                color="primary"
                variant="contained"
                sx={{ width: "100%" }}
                onClick={() => {
                  onboard();
                }}
              >
                Submit
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DawOnboardRetailer;
